import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { homepageProofStudies } from "@/components/equinox/data";

export default function ExpandedSocialProofSection() {
  return (
    <section className="px-6 pb-10 sm:px-8 lg:px-12 lg:pb-14" data-testid="expanded-social-proof-section">
      <div className="mx-auto max-w-7xl">
        <div className="editorial-shell premium-border glass-panel overflow-hidden rounded-[2.4rem] px-5 py-8 sm:px-7 lg:px-8 lg:py-10">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="grid gap-8 lg:grid-cols-[0.76fr_1.24fr] lg:items-end"
          >
            <div>
              <p className="section-kicker" data-testid="expanded-social-proof-kicker">Proven App Growth</p>
              <h2 className="mt-5 max-w-[11ch] font-display text-3xl tracking-[-0.05em] text-white sm:text-4xl lg:text-5xl" data-testid="expanded-social-proof-heading">
                Real Apps. Real Growth. Measurable Results.
              </h2>
              <p className="mt-5 max-w-xl text-sm leading-8 text-white/[0.62]" data-testid="expanded-social-proof-copy">
                We work with ambitious apps to strengthen app-store visibility, reputation, conversion and organic growth.
              </p>
              <div className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-white/[0.54]" data-testid="expanded-social-proof-pill">
                App Reputation + ASO + Organic Growth
              </div>
              <div className="mt-7">
                <Link
                  to="/case-studies"
                  className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-5 py-3 text-sm font-medium text-white/82 transition-transform duration-300 hover:-translate-y-0.5 hover:text-white"
                  data-testid="homepage-view-all-case-studies-link"
                >
                  View All Case Studies
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-3">
              {homepageProofStudies.map((item, index) => (
                <motion.div
                  key={item.client}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: index * 0.06 }}
                  className="glitter-card cinematic-card rounded-[1.8rem] border border-white/[0.08] bg-black/20 px-5 py-5 backdrop-blur-xl"
                  data-testid={`expanded-social-proof-card-${index + 1}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">Client / App</p>
                      <p className="mt-3 font-display text-3xl tracking-[-0.05em] text-white" data-testid={`expanded-social-proof-value-${index + 1}`}>
                        {item.client}
                      </p>
                    </div>
                    <ArrowRight className="mt-1 h-4 w-4 text-blue-200" />
                  </div>
                  <p className="mt-4 text-sm leading-7 text-white/[0.62]">{item.summary}</p>
                  <div className="mt-5 grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
                    {item.metrics.map((metric, metricIndex) => (
                      <div key={`${item.client}-${metric.label}`} className="rounded-[1.25rem] border border-white/[0.08] bg-white/[0.04] p-4" data-testid={`homepage-proof-metric-${index + 1}-${metricIndex + 1}`}>
                        <p className="font-display text-3xl tracking-[-0.05em] text-white">{metric.value}</p>
                        <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-white/[0.44]">{item.client} {metric.label}</p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-5 text-[11px] uppercase tracking-[0.22em] text-blue-100">
                    {item.positioning}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}